// @flow

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, Button, Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { AddAllSuggestedRow } from './AddAllSuggestedRow';
import { FetchingRow } from './FetchingRow';
import LoginRow from './LoginRow';
import NoRenewablesRow from './NoRenewablesRow';
import { SuggestedRow } from './SuggestedRow';
import { RENEWABLE_LIMIT } from './modules';
import {
  addRenewableProductsToCart,
  fetchProductQuotations,
  fetchRenewableProducts,
} from './modules/actions';
import { selectOtherRenewableProducts } from './modules/selectors';
import type { QuotedProduct, ExtendedRenewableProduct } from './types';

export function SuggestedProducts(): React.MixedElement | null {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __ } = translator;
  const authenticated = useAppSelector(selectIsAuthenticatedUser);

  const otherRenewableProducts = useAppSelector(selectOtherRenewableProducts);

  const [loading, setLoading] = useState(false);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [loadingRenewables, setLoadingRenewables] = useState(false);
  const [renewablesLoaded, setRenewablesLoaded] = useState(false);
  const [renewablesFailedToLoad, setRenewablesFailedToLoad] = useState(false);
  const [quotedProducts, setQuotedProducts] = useState<Array<QuotedProduct>>(
    [],
  );

  function setLoadingOver(success: boolean): void {
    setLoadingQuotations(false);
    setLoadingRenewables(false);
    setRenewablesLoaded(success);
    setRenewablesFailedToLoad(!success);
  }

  const limitedEntries = useMemo(
    () => otherRenewableProducts.slice(0, RENEWABLE_LIMIT),
    [otherRenewableProducts],
  );

  const loadRenewables = useCallback(async () => {
    if (!authenticated) {
      return;
    }

    setLoadingQuotations(true);
    setLoadingRenewables(true);
    try {
      const response = await dispatch(fetchRenewableProducts());
      const renewableProducts = response?.data;
      if (!renewableProducts || renewableProducts.length === 0) {
        setLoadingOver(true);
        return;
      }

      setLoadingRenewables(false);
      setRenewablesLoaded(true);

      dispatch(fetchProductQuotations(renewableProducts))
        .catch(() => {} /* pass */)
        .then((newQuotedProducts: Array<QuotedProduct> = []) => {
          setQuotedProducts(newQuotedProducts);
          setLoadingQuotations(false);
        });
    } catch (e) {
      setLoadingOver(false);
    }
  }, [authenticated, dispatch]);

  useEffect(() => {
    if (!loadingRenewables && !loadingQuotations) loadRenewables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { domainList, hostingList } = useMemo(
    () => ({
      domainList: limitedEntries.filter(product => product.type === 'domain'),
      hostingList: limitedEntries.filter(
        product => product.type === 'simple-hosting',
      ),
    }),
    [limitedEntries],
  );

  const handleAddAllToCartClick: (
    evt: SyntheticMouseEvent<>,
  ) => Promise<void> = async (evt: SyntheticMouseEvent<>) => {
    evt.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await dispatch(
        addRenewableProductsToCart(translator, otherRenewableProducts),
      );
    } catch (err) {
      dispatch(notifyDanger(__('Unable to add the products in the cart.')));
    }

    setLoading(false);
  };

  if (loadingRenewables) return <FetchingRow />;

  if (!authenticated) return <LoginRow />;

  if (renewablesFailedToLoad) return null;

  if (!renewablesLoaded && !loadingRenewables) return null;

  if (!otherRenewableProducts.length) return <NoRenewablesRow />;

  return (
    <>
      {/* Add SuggestedProducts button */}
      <AddAllSuggestedRow
        renewableProductCount={otherRenewableProducts.length}
        onClick={handleAddAllToCartClick}
      />

      {/* Suggested domains List */}
      {!!domainList.length && (
        <Base marginBottom="base">
          <TitleRow entries={domainList}>
            {__('%(count)s domains', { count: domainList.length })}
          </TitleRow>
          <ListContainer>
            {domainList.map(entry => (
              <Base tagName="li" key={entry.id} marginBottom="xsmall">
                <SuggestedRow
                  entry={entry}
                  quotedProduct={quotedProducts.find(
                    quotedProduct => quotedProduct.name === entry.name,
                  )}
                  loadingQuotations={loadingQuotations}
                  isAddingToCart={loading}
                />
              </Base>
            ))}
          </ListContainer>
        </Base>
      )}

      {/* Suggested SHS */}
      {!!hostingList.length && (
        <>
          <TitleRow entries={hostingList}>
            {__('%(count)s hosting instances', { count: hostingList.length })}
          </TitleRow>
          <ListContainer>
            {hostingList.map(entry => (
              <Base tagName="li" key={entry.id} marginBottom="xsmall">
                <SuggestedRow
                  entry={entry}
                  quotedProduct={quotedProducts.find(
                    quotedProduct => quotedProduct.name === entry.name,
                  )}
                  loadingQuotations={loadingQuotations}
                  isAddingToCart={loading}
                />
              </Base>
            ))}
          </ListContainer>
        </>
      )}
    </>
  );
}

function ListContainer({
  children,
}: {
  children: React.Node,
}): React.MixedElement {
  return (
    <Base
      tagName="ul"
      listStyleType="none"
      marginX="none"
      marginTop="none"
      marginBottom="base"
      padding="none"
    >
      {children}
    </Base>
  );
}

function TitleRow({
  children,
  entries,
}: {
  children: React.Node,
  entries: Array<ExtendedRenewableProduct>,
}): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const allInTheCart = entries.every(e => !!e.product?.id);

  const handleAddAllToCartClick: (
    evt: SyntheticMouseEvent<>,
  ) => Promise<void> = async (evt: SyntheticMouseEvent<>) => {
    evt.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await dispatch(addRenewableProductsToCart(translator, entries));
    } catch (err) {
      dispatch(notifyDanger(__('Unable to add the products in the cart.')));
    }

    setLoading(false);
  };

  return (
    <Base flexGrow1 flex flexJustifyBetween marginBottom="small">
      <Text>{children}</Text>
      {!allInTheCart && (
        <Button
          onClick={handleAddAllToCartClick}
          loading={loading}
          iconBefore="cart"
          kind="link"
        >
          {__('Add all')}
        </Button>
      )}
    </Base>
  );
}
